import { z } from "zod";
import { normalizeCorporateNumber } from "@/utils/corporateNumber";
import {
  COMMERCIAL_ACQUISITION_STATUSES,
  INDUSTRY_MAJOR_CODES,
  LISTING_FILTERS,
} from "../constants";

export const CORPORATE_NUMBER_FORMAT_ERROR_MESSAGE =
  "法人番号は数字13桁、会社法人等番号は数字12桁で入力してください。";
export const NON_NEGATIVE_INTEGER_ERROR_MESSAGE =
  "0以上の整数を入力してください。";
export const ESTABLISHMENT_YEAR_ERROR_MESSAGE =
  "1〜9999の整数を入力してください。";

const isBlank = (value: string): boolean => value.trim() === "";

const integerStringIn = (
  min: number,
  max: number,
  message: string
): z.ZodEffects<z.ZodString> =>
  z.string().refine((value) => {
    if (isBlank(value)) return true;
    const n = Number(value);
    return Number.isInteger(n) && n >= min && n <= max;
  }, message);

const nonNegativeInteger = integerStringIn(
  0,
  Number.MAX_SAFE_INTEGER,
  NON_NEGATIVE_INTEGER_ERROR_MESSAGE
);
const establishmentYear = integerStringIn(
  1,
  9999,
  ESTABLISHMENT_YEAR_ERROR_MESSAGE
);

/**
 * 法人マスタ検索フォームのスキーマ。すべて任意（未指定は条件にしない）。
 * 数値範囲は入力欄の文字列のまま保持し、API送信時に数値へ変換する。
 */
export const RANGE_ORDER_ERROR_MESSAGE = "下限以上の値を入力してください。";
// 代表者名・役員名の上限（BE も 101 文字以上は 400 にする）
export const PERSON_NAME_MAX_LENGTH = 100;
export const PERSON_NAME_LENGTH_ERROR_MESSAGE = `${PERSON_NAME_MAX_LENGTH}文字以内で入力してください。`;
const personName = z.string().refine(
  // BE（Python）と同じく、サロゲートペアの字も1文字に数える
  (value) => [...value.trim()].length <= PERSON_NAME_MAX_LENGTH,
  PERSON_NAME_LENGTH_ERROR_MESSAGE
);

// 範囲指定（下限・上限）の組。下限が上限を超える指定は送らずに入力欄でエラーにする（BEも400にする）。
const RANGE_PAIRS = [
  ["capitalMin", "capitalMax"],
  ["employeesMin", "employeesMax"],
  ["yearFrom", "yearTo"],
  ["foundingYearFrom", "foundingYearTo"],
  ["salesMin", "salesMax"],
] as const;

export const corporateMasterSearchSchema = z
  .object({
    name: z.string(),
    corporateNumber: z
      .string()
      .refine(
        (value) =>
          isBlank(value) || /^\d{12,13}$/.test(normalizeCorporateNumber(value)),
        CORPORATE_NUMBER_FORMAT_ERROR_MESSAGE
      ),
    industry: z.array(z.enum(INDUSTRY_MAJOR_CODES)),
    prefecture: z.array(z.string()),
    city: z.array(z.string()),
    kind: z.array(z.string()),
    capitalMin: nonNegativeInteger,
    capitalMax: nonNegativeInteger,
    employeesMin: nonNegativeInteger,
    employeesMax: nonNegativeInteger,
    yearFrom: establishmentYear,
    yearTo: establishmentYear,
    foundingYearFrom: establishmentYear,
    foundingYearTo: establishmentYear,
    listing: z.union([z.literal(""), z.enum(LISTING_FILTERS)]),
    commercialStatus: z.array(z.enum(COMMERCIAL_ACQUISITION_STATUSES)),
    salesMin: nonNegativeInteger,
    salesMax: nonNegativeInteger,
    representative: personName,
    registryOfficerName: personName,
    includeFormerOfficers: z.boolean(),
    registryTag: z.array(z.string()),
    excludeAmbiguousRegistryTag: z.boolean(),
  })
  .superRefine((values, ctx) => {
    RANGE_PAIRS.forEach(([lower, upper]) => {
      if (isBlank(values[lower]) || isBlank(values[upper])) return;
      if (Number(values[lower]) > Number(values[upper])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [upper],
          message: RANGE_ORDER_ERROR_MESSAGE,
        });
      }
    });
  });

export type CorporateMasterSearchConditions = z.infer<
  typeof corporateMasterSearchSchema
>;
